import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Label/value pair row for read-only detail views — Figma "Label list" (master 1413:36125,
 * onboarding-review contract §4). Two-column grid item by default; `fullWidth` spans the full
 * grid row (long values) and `multiline` disables the single-line truncate (wrapped values).
 *
 * Presentational only: data comes in via inputs, nothing is fetched here. Colors come from the
 * `--dd-accent` (label) and `--dd-card-border` (divider) design tokens plus the `text-default`
 * Fuse utility (value) — see `src/styles/components/_forms.scss` / `_status-badges.scss`. Never
 * hardcode these so dark mode keeps working.
 */
@Component({
  selector: 'dd-label-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-2 pb-3 border-b border-[color:var(--dd-card-border)] min-w-0',
    // col-span-full, not col-span-2: below the sm breakpoint the parent grid is grid-cols-1,
    // where span 2 forces an implicit second track and narrows every sibling row by the
    // column gap. Full-row span is identical on the 2-column desktop grid.
    '[class.col-span-full]': 'fullWidth()',
  },
  template: `
    <span class="text-[0.75rem] font-medium leading-none text-[color:var(--dd-accent)]">{{ label() }}</span>
    <!-- sentry-mask on the VALUE only: this is where entry data lands (names, addresses, e-mail
         addresses), while the label above it is interface text that a replay needs to stay
         readable. Masking here is what makes blanket maskAllText avoidable outside production. -->
    <span
      class="text-[0.9375rem] font-medium leading-[1.4] text-default sentry-mask"
      [class.truncate]="!multiline()"
      [attr.data-cy]="dataCy()">
      {{ value() ?? '—' }}
    </span>
  `,
})
export class LabelListComponent {
  readonly label = input.required<string>();
  readonly value = input<string | null>(null);
  /** Disables the single-line truncate — long values wrap over multiple lines instead. */
  readonly multiline = input(false);
  /** Spans the parent grid's full row (both columns on the 2-column desktop grid). */
  readonly fullWidth = input(false);
  /** Optional data-cy on the value element, for callers that need a stable test hook. */
  readonly dataCy = input<string | null>(null);
}
